import React, { useState } from 'react';
import { LuChevronDown } from 'react-icons/lu';
import { PillCell } from '@/components/atoms';
import { Text } from '@/components/atoms/AppText';
import type { PillPreset } from '@/components/organism/StatusPills/pillCell.types';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
  APP_GRADIENT,
} from '@/constant/theme/colors.constant';
import type {
  ServiceCardStyleProps,
  FieldRow,
  ServiceField,
  SubItem,
} from './serviceCard.types';
import { StatCell } from './StatCell';

export type { FieldRow, ServiceField, SubItem } from './serviceCard.types';

// ─── Field renderer ───────────────────────────────────────────────────────────

const renderField = (field: ServiceField, idx: number) => {
  switch (field.type) {
    case 'text':
      return (
        <StatCell
          key={idx}
          label={field.label}
          labelIcon={field.labelIcon}
          value={field.value || undefined}
          mono={field.mono}
        />
      );
    case 'pill':
      return (
        <StatCell
          key={idx}
          label={field.label}
          labelIcon={field.labelIcon}
          value={
            field.value ? (
              <PillCell
                value={field.value}
                preset={field.preset}
                shape="badge"
                size="text-xs"
                className={
                  field.mono ? 'font-mono break-all leading-relaxed' : ''
                }
                config={field.pillConfig}
                customStyle={{ padding: '0.25rem 0.625rem' }}
              />
            ) : undefined
          }
        />
      );
    case 'pill-list':
      return (
        <StatCell
          key={idx}
          label={field.label}
          labelIcon={field.labelIcon}
          value={
            <div className="flex flex-wrap gap-1.5">
              {(field.values ?? []).map((v) => (
                <PillCell
                  key={v}
                  value={v}
                  shape="badge"
                  size="text-xs"
                  config={field.pillConfig}
                  customStyle={{ padding: '0.25rem 0.625rem' }}
                />
              ))}
            </div>
          }
        />
      );
    case 'kv':
      return (
        <StatCell
          key={idx}
          label={field.label}
          labelIcon={field.labelIcon}
          value={field.value ?? undefined}
        />
      );
  }
};

/** Exported helper \u2014 renders a single FieldRow band */
export const renderFieldRow = (row: FieldRow, rowIdx: number) => {
  const cols: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  };
  const colClass =
    cols[row.fields.length] ?? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  return (
    <div
      key={rowIdx}
      className={`grid ${colClass} divide-y sm:divide-y-0 sm:divide-x ${APP_BORDER.divideSoft} ${row.altBg ? APP_BG.overlay : ''}`}
    >
      {row.fields.map((f, i) => renderField(f, i))}
    </div>
  );
};

// ─── Shared header ────────────────────────────────────────────────────────────

interface CardHeaderProps {
  icon: React.ReactNode;
  label: string;
  enabled: boolean;
  enabledLabel: string;
  disabledLabel: string;
  statusPreset: PillPreset;
  enabledHeaderBgClass: string;
}

const CardHeader = ({
  icon,
  label,
  enabled,
  enabledLabel,
  disabledLabel,
  statusPreset,
  enabledHeaderBgClass,
}: CardHeaderProps) => (
  <div
    className={`flex items-center justify-between gap-2.5 px-4 py-3 border-b ${APP_BORDER.soft} ${enabled ? enabledHeaderBgClass : APP_BG.canvas}`}
  >
    <div className="flex min-w-0 items-center gap-2.5">
      <div
        className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${enabled ? 'text-white shadow-sm' : `${APP_SEMANTIC.neutral.bg} ${APP_SEMANTIC.neutral.text}`}`}
        style={enabled ? { background: APP_GRADIENT.brandVar } : undefined}
      >
        {icon}
      </div>
      <Text
        as="span"
        size="md"
        weight="bold"
        color="default"
        className="min-w-0 break-words"
      >
        {label}
      </Text>
    </div>
    <div className="flex shrink-0 items-center">
      <PillCell
        value={enabled ? enabledLabel : disabledLabel}
        preset={statusPreset}
        dot
        size="text-xs"
        customStyle={{ padding: '0.25rem 0.625rem' }}
      />
    </div>
  </div>
);

// ─── ServiceCard ──────────────────────────────────────────────────────────────

export interface ServiceCardProps extends ServiceCardStyleProps {
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  /** Flat field rows \u2014 static data card body. Ignored when subItems is provided. */
  rows?: FieldRow[];
  /** Accordion sub-items \u2014 switches body to expandable rows mode. */
  subItems?: SubItem[];
  /** Tailwind bg class for an expanded + enabled accordion row */
  subEnabledRowBgClass?: string;
  /** Tailwind class for the enabled dot in accordion rows */
  subEnabledDotClass?: string;
  /** Escape hatch: custom body (used only when rows and subItems are absent) */
  children?: React.ReactNode;
  /** data-testid forwarded to the root wrapper div */
  testId?: string;
}

/**
 * ServiceCard \u2014 single unified card component.
 *
 * Modes (checked in order):
 *   1. subItems  \u2192 accordion expandable rows
 *   2. rows      \u2192 flat field-row grid
 *   3. children  \u2192 custom escape hatch
 */
const ServiceCard = ({
  label,
  icon,
  enabled,
  rows,
  subItems,
  children,
  testId,
  enabledLabel = ORGANISM_PROPS.FEATURE_FLAG_CARD.ENABLED,
  disabledLabel = ORGANISM_PROPS.FEATURE_FLAG_CARD.DISABLED,
  statusPreset = 'statusEnabled',
  enabledBorderClass = 'border-primary/40 dark:border-primary/30',
  enabledHeaderBgClass = 'bg-primary/5 dark:bg-primary/10',
  subEnabledRowBgClass = 'bg-primary/5 dark:bg-primary/10',
  subEnabledDotClass = 'bg-primary',
}: ServiceCardProps) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div
      data-testid={testId ?? ''}
      className={`rounded-xl border overflow-hidden ${enabled ? `${enabledBorderClass} shadow-sm` : APP_BORDER.default}`}
    >
      <CardHeader
        icon={icon}
        label={label}
        enabled={enabled}
        enabledLabel={enabledLabel}
        disabledLabel={disabledLabel}
        statusPreset={statusPreset}
        enabledHeaderBgClass={enabledHeaderBgClass}
      />

      <div className={`${APP_BG.base} divide-y ${APP_BORDER.divideSoft}`}>
        {subItems
          ? subItems.map((item) => {
              const isOpen = expanded === item.key;
              return (
                <div key={item.key}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : item.key)}
                    className={`w-full flex items-center justify-between gap-2.5 px-4 py-3 border-b ${APP_BORDER.soft} transition-colors ${isOpen ? (item.enabled ? subEnabledRowBgClass : APP_BG.canvas) : `${APP_SEMANTIC.neutral.hover}`}`}
                  >
                    <div className="flex min-w-0 items-center gap-2.5 text-left">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${item.enabled ? subEnabledDotClass : APP_SEMANTIC.neutral.dot}`}
                      />
                      <Text
                        as="span"
                        size="sm"
                        weight="bold"
                        color="soft"
                        tracking="wide"
                        className="min-w-0 break-words"
                      >
                        {item.key}
                      </Text>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <PillCell
                        value={item.enabled ? enabledLabel : disabledLabel}
                        preset={statusPreset}
                        dot
                        size="text-xs"
                        customStyle={{ padding: '0.25rem 0.625rem' }}
                      />
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center border ${APP_BORDER.default} ${APP_BG.base} shadow-sm transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                      >
                        <LuChevronDown
                          className={`w-2.5 h-2.5 ${APP_SEMANTIC.neutral.text}`}
                          strokeWidth={2.5}
                        />
                      </span>
                    </div>
                  </button>
                  {isOpen && item.rows && item.rows.length > 0 && (
                    <div
                      className={`border-t border-dashed ${APP_BORDER.default} divide-y ${APP_BORDER.divideSoft}`}
                    >
                      {item.rows.map((row, i) => renderFieldRow(row, i))}
                    </div>
                  )}
                </div>
              );
            })
          : rows
            ? rows.map((row, i) => renderFieldRow(row, i))
            : children}
      </div>
    </div>
  );
};

export default ServiceCard;
